/* eslint-disable @nx/enforce-module-boundaries */
import { RenderIcon } from "admin-ui/lib/icons"
import ErrorPage from "modules/error"
import { Suspense } from "react"
import {
  BrowserRouter,
  Outlet,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom"
import configRoutes, { IRoute, RoutePath } from "./config"
import CenterElement from "libraries/common/center-element"
import useProfile from "hooks/useProfile"
import { hasRoutePermission, getFallbackRoute } from "utils/route-permissions"

// Protected Route Component
const ProtectedRoute = ({
  route,
  children,
}: {
  route: IRoute
  children: React.ReactNode
}) => {
  const { user, role, isLoading, isShowChat } = useProfile()
  const vendorType = user?.vendor?.vendor_type
  const location = useLocation()

  // If still loading, show loading state
  if (isLoading) {
    return (
      <CenterElement>
        <RenderIcon name="spinner" />
      </CenterElement>
    )
  }

  // If no user, redirect to login
  if (!user) {
    return <Navigate to={RoutePath.Login} replace />
  }

  const hasPermission = hasRoutePermission(route, {
    role,
    vendorType,
    isShowChat,
  })

  if (!hasPermission) {
    const fallbackRoute = getFallbackRoute({ role, vendorType })

    // Prevent infinite redirects by checking if we're already on the fallback route
    if (location.pathname === fallbackRoute) {
      // If we're already on the fallback route and still don't have permission, redirect to login
      return <Navigate to={RoutePath.Login} replace />
    }

    return <Navigate to={fallbackRoute} replace />
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>
}

export default function MasterRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {renderRoutes(configRoutes.filter((item) => item.isProtected))}
        {renderPublicRoutes(configRoutes.filter((item) => !item.isProtected))}
      </Routes>
    </BrowserRouter>
  )
}

const renderRoutes = (routes: IRoute[]) => {
  if (!routes || routes.length <= 0) return null

  return (
    <>
      {routes.map((item, index) => {
        const Element = item.element
        const ErrorElement = item.errorElement
        const childRoutes = item.children || []
        return (
          <Route
            key={index}
            path={item.path}
            element={
              <Suspense
                fallback={
                  <CenterElement>
                    <RenderIcon name="spinner" />
                  </CenterElement>
                }
              >
                <ProtectedRoute route={item}>
                  {Element ? <Element /> : <Outlet />}
                </ProtectedRoute>
              </Suspense>
            }
            errorElement={ErrorElement ? <ErrorElement /> : <ErrorPage />}
          >
            {childRoutes.length > 0 && renderRoutes(childRoutes)}
          </Route>
        )
      })}
    </>
  )
}

const renderPublicRoutes = (routes: IRoute[]) => {
  if (!routes || routes.length <= 0) return null
  return (
    <>
      {routes.map((item, index) => {
        const Element = item.element
        const ErrorElement = item.errorElement
        const childRoutes = item.children || []
        return (
          <Route
            key={index}
            path={item.path}
            element={
              <Suspense
                fallback={
                  <CenterElement>
                    <RenderIcon name="spinner" />
                  </CenterElement>
                }
              >
                {Element ? <Element /> : <Outlet />}
              </Suspense>
            }
            errorElement={ErrorElement ? <ErrorElement /> : <ErrorPage />}
          >
            {childRoutes.length > 0 && renderPublicRoutes(childRoutes)}
          </Route>
        )
      })}
    </>
  )
}
